/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	ViewEncapsulation,
	OnInit,
	AfterViewInit,
	OnDestroy,
	ViewChild,
	ChangeDetectionStrategy,
	SecurityContext,
	Output,
	EventEmitter,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { UntypedFormControl } from '@angular/forms';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, switchMap, map, tap, distinctUntilChanged } from 'rxjs/operators';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';

import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { DraggableContainerComponent } from '../draggable-container/draggable-container.component';
import { Base64 } from 'js-base64';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import firebase from 'firebase/compat/app';
import { logger } from '@touchplan/logging';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';

function identifyHostFromUrl(url) {
	let parsedUrl: URL;
	try {
		parsedUrl = new URL(url);
	} catch (e) {
		logger.log(e);
		return null;
	}

	if (parsedUrl.host && parsedUrl.host.match(/zoom\.us/)) {
		return 'zoom';
	}
	if (parsedUrl.host && parsedUrl.host.match(/jit\.si/)) {
		return 'jitsi';
	}
	return null;
}

function makeZoomUrl(meetingId) {
	let otherParse: URL;
	try {
		otherParse = new URL(meetingId);
	} catch (e) {
		logger.log(e);
		return null;
	}

	const host = identifyHostFromUrl(meetingId);
	const params = otherParse.searchParams;

	if (host === 'zoom') {
		otherParse.pathname = otherParse.pathname.replace(/\/j\/(\d+)/, '/wc/$1/join');
		params.set('prefer', '1');
	}

	otherParse.search = params.toString();

	return otherParse.toString();
}

@Component({
	selector: 'meeting-popup',
	templateUrl: './meeting-popup.component.html',
	styleUrls: ['./meeting-popup.component.scss'],
	styles: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None, //TODO - this is bad
})
export class MeetingPopup implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('draggableContainer', { static: false }) public draggableContainer: DraggableContainerComponent;

	@Output() public closePopup = new EventEmitter();

	// private meetingId = '957474608';
	private shutdownSubject$ = new Subject<void>();

	private sharedEmbedUrlRef: firebase.database.Reference;

	protected url = new UntypedFormControl('');
	public videoHost = new UntypedFormControl('zoom');

	public dropdownOptions = [
		{ label: 'Jit.si', value: 'jitsi' },
		{ label: 'Zoom.us', value: 'zoom' },
	];

	constructor(
		private sanitizer: DomSanitizer,
		public planState: PlanState,
		public planAccessCheck: PlanAccessCheck
	) {}

	// private firebaseSafify(url: string) {

	// }
	// private unFirebaseSafify(url: string) {

	// }

	public meetingJoined$ = new BehaviorSubject(false);

	public sharedUrl$ = this.planState.stateChange$.pipe(
		filter((event) => event === StateEvents.FULLY_LOADED),
		switchMap((event) => {
			return this.planState.plan.object$;
		}),
		map((plan) => (plan && plan.view.sharedEmbedUrl ? plan.view.sharedEmbedUrl : null))
		// tap(url => logger.log('!!!!!!!!!!!! plan embed url', url))
	);

	//this needs to be rethought since only some hosts need it...
	public userProcessedUrl$ = combineLatest([
		this.planState.authedProjectMember$.pipe(
			// tap(you => logger.log('you', you)),
			map((authedProjectMember) =>
				authedProjectMember && authedProjectMember.view.user
					? authedProjectMember.view.user.firstName
					: 'placeholder'
			),
			distinctUntilChanged() //prevent weird duplicate emissions
		),
		this.sharedUrl$,
	]).pipe(
		map((things) => {
			if (!things[1]) {
				return null;
			}
			const host = identifyHostFromUrl(things[1]);

			if (host === 'zoom') {
				const encodedName = Base64.encode(things[0]);
				return `${things[1]}&un=${encodedName}`;
			} else {
				return things[1];
			}
		}),
		tap((you) => logger.log('you', you))
		// distinctUntilChanged()
	);

	public userProcessedSanitizedUrl$ = this.userProcessedUrl$.pipe(map((url) => this.sanitizeUrl(url)));

	public ngOnInit() {
		const planPath = fancyFirebaseGetters.getPlanPath(this.planState.projectId, this.planState.planId);
		this.sharedEmbedUrlRef = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(planPath, this.planState.projectId)
			.child('sharedEmbedUrl');

		analyticsEventsService.openedVideoMeeting(this.planState.planId);
		// this.sharedUrl$.pipe(
		// 	filter( url => !!url ),
		// 	takeUntil(this.shutdownSubject$)
		// ).subscribe(url => {
		// 	this.url.setValue(url);
		// });
	}

	public ngAfterViewInit() {
		this.draggableContainer.setWidth(500);
		this.draggableContainer.setHeight(400);
		this.draggableContainer.setX(0);
		this.draggableContainer.setY(0);
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	public setUrl(meetingId: string) {
		if (!meetingId) {
			Logging.warning('A meeting url must be entered');
			return;
		}

		const url = makeZoomUrl(meetingId);
		if (!url) {
			Logging.warning('The provided link is not a valid zoom url ' + meetingId);
			return;
		}

		const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, url);

		this.sharedEmbedUrlRef.set(url, (error) => {
			if (!error) {
				this.joinMeeting();
			} else {
				Logging.warning('Cannot save meeting id.');
			}
		});

		analyticsEventsService.createdVideoMeeting(this.planState.planId);
	}

	public setJitsiUrl() {
		if (!this.planState.projectId || !this.planState.planId) {
			Logging.warning('Something went wrong, try refreshing');
			return;
		}
		const base = 'https://meet.jit.si/';
		const tpId = this.planState.projectId + ',' + this.planState.planId;

		const combination = base + tpId;

		this.setUrl(combination);
	}

	public sanitizeUrl(url: string) {
		return this.sanitizer.bypassSecurityTrustResourceUrl(url);
		// return this.sanitizer.bypassSecurityTrustResourceUrl('https://zoom.us/wc/'+ this.meetingId +'/join?prefer=1&un=dG9tbXk=');
	}

	public joinMeeting() {
		this.meetingJoined$.next(true);
		analyticsEventsService.joinedVideoMeeting(this.planState.planId);
	}

	public endMeeting() {
		this.sharedEmbedUrlRef.set(null);
		this.meetingJoined$.next(false);
	}

	public close() {
		this.closePopup.emit();
	}
}
